import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDarkMode } from '../../../contexts/DarkModeContext';
import { functions, db } from '../../../firebase';
import { httpsCallable } from 'firebase/functions';
import { doc, getDoc } from 'firebase/firestore';
import { Building2, MapPin, Mail, ArrowLeft, Loader, Users } from 'lucide-react';
import AddressAutocomplete from '../AddressAutocomplete';

const EditMosque = () => {
  const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const { mosqueId } = useParams();
  const navigate = useNavigate();
  const { isDarkMode } = useDarkMode();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [mosque, setMosque] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    displayName: '',
    street: '',
    postcode: '',
    city: '',
    country: '',
    countryCode: '',
    email: '',
    phone: '',
    website: '',
    photo: '',
    prayerTimeSource: 'aladhan',
    latitude: '',
    longitude: '',
    calculationMethod: '3',
    school: '0',
    jumahEnabled: true,
    jumahTime: ''
  });

  const [mosqueAdmins, setMosqueAdmins] = useState([]);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [selectedAdminId, setSelectedAdminId] = useState('');

  const calculationMethods = [
    { value: '1', label: 'University of Islamic Sciences, Karachi' },
    { value: '2', label: 'Islamic Society of North America (ISNA)' },
    { value: '3', label: 'Muslim World League' },
    { value: '4', label: 'Umm Al-Qura University, Makkah' },
    { value: '5', label: 'Egyptian General Authority of Survey' },
    { value: '7', label: 'Institute of Geophysics, University of Tehran' },
    { value: '8', label: 'Gulf Region' },
    { value: '9', label: 'Kuwait' },
    { value: '10', label: 'Qatar' },
    { value: '11', label: 'Majlis Ugama Islam Singapura, Singapore' },
    { value: '12', label: 'Union Organization islamic de France' },
    { value: '13', label: 'Diyanet İşleri Başkanlığı, Turkey' },
    { value: '14', label: 'Spiritual Administration of Muslims of Russia' }
  ];

  const schools = [
    { value: '0', label: 'Shafi' },
    { value: '1', label: 'Hanafi' }
  ];

  const countryCodes = [
    { code: 'NL', name: 'Netherlands' },
    { code: 'BE', name: 'Belgium' },
    { code: 'DE', name: 'Germany' },
    { code: 'FR', name: 'France' },
    { code: 'UK', name: 'United Kingdom' }, // catalogue/index contract uses UK (ISO GB)
    { code: 'US', name: 'United States' },
    { code: 'CA', name: 'Canada' },
    { code: 'TR', name: 'Turkey' },
    { code: 'SA', name: 'Saudi Arabia' },
    { code: 'AE', name: 'United Arab Emirates' }
  ];

  useEffect(() => {
    loadMosque();
    loadApprovedAdmins();
  }, [mosqueId]);

  const loadMosque = async () => {
    try {
      const mosqueDoc = await getDoc(doc(db, 'mosques', mosqueId));
      if (mosqueDoc.exists()) {
        const data = mosqueDoc.data();
        setMosque(data);
        setFormData({
          name: data.name || '',
          displayName: data.displayName || '',
          street: data.location?.street || '',
          postcode: data.location?.postcode || '',
          city: data.location?.city || '',
          country: data.location?.country || '',
          countryCode: (data.location?.countryCode || '').toUpperCase() === 'GB' ? 'UK' : (data.location?.countryCode || ''),
          email: data.email || '',
          phone: data.phone || '',
          website: data.website || '',
          photo: data.photo || '',
          prayerTimeSource: data.prayerTimeSource || 'aladhan',
          latitude: data.location?.coordinates?.lat || '',
          longitude: data.location?.coordinates?.lng ?? data.location?.coordinates?.lon ?? '',
          calculationMethod: data.aladhanSettings?.calculationMethod?.toString() || '3',
          school: data.aladhanSettings?.school?.toString() || '0',
          jumahEnabled: data.features?.jumahEnabled ?? true,
          jumahTime: data.features?.jumahTime || ''
        });
      } else {
        setError('Mosque not found');
      }
    } catch (err) {
      console.error('Error loading mosque:', err);
      setError('Failed to load mosque');
    } finally {
      setLoading(false);
    }
  };

  const loadApprovedAdmins = async () => {
    try {
      const getAllMosqueAdmins = httpsCallable(functions, 'getAllMosqueAdmins');
      const result = await getAllMosqueAdmins({ status: 'approved' });
      // Filter out admins that already have a mosque
      const availableAdmins = result.data.admins.filter(admin => !admin.mosqueId);
      setMosqueAdmins(availableAdmins);
    } catch (err) {
      console.error('Error loading admins:', err);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleAddressSelect = (addressData) => {
    setFormData(prev => ({
      ...prev,
      street: addressData.street,
      postcode: addressData.postcode,
      city: addressData.city,
      country: addressData.country,
      countryCode: addressData.countryCode === 'GB' ? 'UK' : addressData.countryCode, // Places returns ISO GB
      latitude: addressData.latitude?.toString() || '',
      longitude: addressData.longitude?.toString() || ''
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');

    try {
      const updateMosque = httpsCallable(functions, 'updateMosque');
      const normalizedName = formData.name.trim();
      const normalizedEmail = formData.email.trim();
      const normalizedPhone = formData.phone.trim();
      const normalizedWebsite = formData.website.trim();
      if (!normalizedName) {
        setError('Mosque name is required');
        setSaving(false);
        return;
      }
      if (normalizedEmail && !EMAIL_REGEX.test(normalizedEmail)) {
        setError('Please enter a valid email address');
        setSaving(false);
        return;
      }
      const latitude = parseFloat(formData.latitude);
      const longitude = parseFloat(formData.longitude);

      if (
        formData.prayerTimeSource === 'aladhan' &&
        (!Number.isFinite(latitude) || !Number.isFinite(longitude))
      ) {
        setError('Valid latitude and longitude are required when using Aladhan prayer times');
        setSaving(false);
        return;
      }

      const location = {
        street: formData.street,
        postcode: formData.postcode,
        city: formData.city,
        country: formData.country,
        countryCode: formData.countryCode,
        coordinates: formData.latitude && formData.longitude ? {
          lat: latitude,
          lng: longitude
        } : null
      };

      const aladhanSettings = formData.prayerTimeSource === 'aladhan' ? {
        calculationMethod: parseInt(formData.calculationMethod, 10),
        latitude: latitude,
        longitude: longitude,
        school: parseInt(formData.school, 10),
        midnightMode: 0
      } : null;

      const features = {
        jumahEnabled: formData.jumahEnabled,
        jumahTime: formData.jumahTime || null,
        announcementsEnabled: mosque.features?.announcementsEnabled ?? true,
        newsEnabled: mosque.features?.newsEnabled ?? false,
        calendarEnabled: mosque.features?.calendarEnabled ?? false
      };

      await updateMosque({
        mosqueId,
        updates: {
          name: normalizedName,
          displayName: formData.displayName?.trim() || normalizedName,
          location,
          email: normalizedEmail,
          phone: normalizedPhone || null,
          website: normalizedWebsite || null,
          photo: formData.photo || null,
          photos: formData.photo ? [formData.photo] : [],
          prayerTimeSource: formData.prayerTimeSource,
          aladhanSettings,
          features
        }
      });

      navigate('/admin/mosques');
    } catch (err) {
      console.error('Error updating mosque:', err);
      setError(err.message || 'Failed to update mosque');
    } finally {
      setSaving(false);
    }
  };

  const handleTransfer = async () => {
    if (!selectedAdminId) return;

    setSaving(true);
    setError('');

    try {
      const transferMosqueToAdmin = httpsCallable(functions, 'transferMosqueToAdmin');
      await transferMosqueToAdmin({ mosqueId, adminId: selectedAdminId });
      setShowTransferModal(false);
      navigate('/admin/mosques');
    } catch (err) {
      console.error('Error transferring mosque:', err);
      setError(err.message || 'Failed to transfer mosque');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader className="w-8 h-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/admin/mosques')}
          className={`flex items-center gap-2 mb-4 text-sm transition-colors ${
            isDarkMode
              ? 'text-gray-400 hover:text-white'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Mosques
        </button>
        <div className="flex items-center justify-between">
          <div>
            <h1 className={`text-2xl font-bold ${
              isDarkMode ? 'text-white' : 'text-gray-900'
            }`}>
              Edit Mosque
            </h1>
            <p className={`mt-1 text-sm ${
              isDarkMode ? 'text-gray-400' : 'text-gray-600'
            }`}>
              Update mosque information and settings
            </p>
          </div>

          {/* Transfer Button */}
          {!mosque?.adminId && mosqueAdmins.length > 0 && (
            <button
              onClick={() => setShowTransferModal(true)}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl transition-colors ${
                isDarkMode
                  ? 'bg-blue-500/20 text-blue-400 hover:bg-blue-500/30'
                  : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
              }`}
            >
              <Users className="w-4 h-4" />
              Transfer to Admin
            </button>
          )}
        </div>
      </div>

      {/* Admin Info if assigned */}
      {mosque?.adminId && (
        <div className={`mb-6 p-4 rounded-xl border ${
          isDarkMode
            ? 'bg-emerald-900/20 border-emerald-500/30'
            : 'bg-emerald-50 border-emerald-200'
        }`}>
          <p className={`text-sm ${
            isDarkMode ? 'text-emerald-400' : 'text-emerald-700'
          }`}>
            This mosque is managed by a mosque administrator
          </p>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className={`mb-6 p-4 rounded-xl border ${
          isDarkMode
            ? 'bg-red-900/20 border-red-500/30 text-red-400'
            : 'bg-red-50 border-red-200 text-red-600'
        }`}>
          {error}
        </div>
      )}

      {/* Form - Same as CreateMosque */}
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Information */}
        <div className={`p-6 rounded-[24px] border ${
          isDarkMode
            ? 'bg-[#111111] border-gray-800'
            : 'bg-white border-gray-200'
        }`}>
          <h2 className={`text-lg font-semibold mb-4 flex items-center gap-2 ${
            isDarkMode ? 'text-white' : 'text-gray-900'
          }`}>
            <Building2 className="w-5 h-5" />
            Basic Information
          </h2>

          <div className="space-y-4">
            <div>
              <label className={`block text-sm font-medium mb-2 ${
                isDarkMode ? 'text-gray-300' : 'text-gray-700'
              }`}>
                Search Mosque Name
              </label>
              <AddressAutocomplete
                onPlaceSelect={(data) => {
                  // Update mosque name
                  setFormData(prev => ({ ...prev, name: data.placeName || data.formattedAddress }));
                  // Update address fields
                  handleAddressSelect(data);
                }}
                initialValue={formData.name}
                placeholder="Start typing mosque name..."
                searchType="mosque"
              />
              <p className={`mt-1.5 text-xs ${
                isDarkMode ? 'text-gray-500' : 'text-gray-500'
              }`}>
                Search for mosque to auto-fill address details
              </p>
            </div>

            <div>
              <label className={`block text-sm font-medium mb-2 ${
                isDarkMode ? 'text-gray-300' : 'text-gray-700'
              }`}>
                Mosque Name *
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
                className={`w-full px-4 py-2.5 rounded-xl border transition-colors ${
                  isDarkMode
                    ? 'bg-[#0A0A0A] border-gray-700 text-white focus:border-emerald-500'
                    : 'bg-white border-gray-300 text-gray-900 focus:border-emerald-500'
                } focus:outline-none`}
                placeholder="e.g., Grote Moskee Amsterdam"
              />
            </div>

            <div>
              <label className={`block text-sm font-medium mb-2 ${
                isDarkMode ? 'text-gray-300' : 'text-gray-700'
              }`}>
                Display Name (optional)
              </label>
              <input
                type="text"
                name="displayName"
                value={formData.displayName}
                onChange={handleInputChange}
                className={`w-full px-4 py-2.5 rounded-xl border transition-colors ${
                  isDarkMode
                    ? 'bg-[#0A0A0A] border-gray-700 text-white focus:border-emerald-500'
                    : 'bg-white border-gray-300 text-gray-900 focus:border-emerald-500'
                } focus:outline-none`}
                placeholder="Alternative display name"
              />
            </div>
          </div>
        </div>

        {/* Location */}
        <div className={`p-6 rounded-[24px] border ${
          isDarkMode
            ? 'bg-[#111111] border-gray-800'
            : 'bg-white border-gray-200'
        }`}>
          <h2 className={`text-lg font-semibold mb-4 flex items-center gap-2 ${
            isDarkMode ? 'text-white' : 'text-gray-900'
          }`}>
            <MapPin className="w-5 h-5" />
            Location
          </h2>

          <div className="space-y-4">
            <div>
              <label className={`block text-sm font-medium mb-2 ${
                isDarkMode ? 'text-gray-300' : 'text-gray-700'
              }`}>
                Street Address *
              </label>
              <input
                type="text"
                name="street"
                value={formData.street}
                onChange={handleInputChange}
                required
                className={`w-full px-4 py-2.5 rounded-xl border transition-colors ${
                  isDarkMode
                    ? 'bg-[#0A0A0A] border-gray-700 text-white focus:border-emerald-500'
                    : 'bg-white border-gray-300 text-gray-900 focus:border-emerald-500'
                } focus:outline-none`}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={`block text-sm font-medium mb-2 ${
                  isDarkMode ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  Postcode *
                </label>
                <input
                  type="text"
                  name="postcode"
                  value={formData.postcode}
                  onChange={handleInputChange}
                  required
                  className={`w-full px-4 py-2.5 rounded-xl border transition-colors ${
                    isDarkMode
                      ? 'bg-[#0A0A0A] border-gray-700 text-white focus:border-emerald-500'
                      : 'bg-white border-gray-300 text-gray-900 focus:border-emerald-500'
                  } focus:outline-none`}
                />
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 ${
                  isDarkMode ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  City *
                </label>
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleInputChange}
                  required
                  className={`w-full px-4 py-2.5 rounded-xl border transition-colors ${
                    isDarkMode
                      ? 'bg-[#0A0A0A] border-gray-700 text-white focus:border-emerald-500'
                      : 'bg-white border-gray-300 text-gray-900 focus:border-emerald-500'
                  } focus:outline-none`}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={`block text-sm font-medium mb-2 ${
                  isDarkMode ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  Country *
                </label>
                <input
                  type="text"
                  name="country"
                  value={formData.country}
                  onChange={handleInputChange}
                  required
                  className={`w-full px-4 py-2.5 rounded-xl border transition-colors ${
                    isDarkMode
                      ? 'bg-[#0A0A0A] border-gray-700 text-white focus:border-emerald-500'
                      : 'bg-white border-gray-300 text-gray-900 focus:border-emerald-500'
                  } focus:outline-none`}
                />
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 ${
                  isDarkMode ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  Country Code *
                </label>
                <input
                  type="text"
                  name="countryCode"
                  value={formData.countryCode}
                  onChange={handleInputChange}
                  required
                  className={`w-full px-4 py-2.5 rounded-xl border transition-colors ${
                    isDarkMode
                      ? 'bg-[#0A0A0A] border-gray-700 text-white focus:border-emerald-500'
                      : 'bg-white border-gray-300 text-gray-900 focus:border-emerald-500'
                  } focus:outline-none`}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={`block text-sm font-medium mb-2 ${
                  isDarkMode ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  Latitude
                </label>
                <input
                  type="number"
                  step="any"
                  name="latitude"
                  value={formData.latitude}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-2.5 rounded-xl border transition-colors ${
                    isDarkMode
                      ? 'bg-[#0A0A0A] border-gray-700 text-white focus:border-emerald-500'
                      : 'bg-white border-gray-300 text-gray-900 focus:border-emerald-500'
                  } focus:outline-none`}
                />
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 ${
                  isDarkMode ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  Longitude
                </label>
                <input
                  type="number"
                  step="any"
                  name="longitude"
                  value={formData.longitude}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-2.5 rounded-xl border transition-colors ${
                    isDarkMode
                      ? 'bg-[#0A0A0A] border-gray-700 text-white focus:border-emerald-500'
                      : 'bg-white border-gray-300 text-gray-900 focus:border-emerald-500'
                  } focus:outline-none`}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Contact Information */}
        <div className={`p-6 rounded-[24px] border ${
          isDarkMode
            ? 'bg-[#111111] border-gray-800'
            : 'bg-white border-gray-200'
        }`}>
          <h2 className={`text-lg font-semibold mb-4 flex items-center gap-2 ${
            isDarkMode ? 'text-white' : 'text-gray-900'
          }`}>
            <Mail className="w-5 h-5" />
            Contact Information
          </h2>

          <div className="space-y-4">
            <div>
              <label className={`block text-sm font-medium mb-2 ${
                isDarkMode ? 'text-gray-300' : 'text-gray-700'
              }`}>
                Email Address
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                className={`w-full px-4 py-2.5 rounded-xl border transition-colors ${
                  isDarkMode
                    ? 'bg-[#0A0A0A] border-gray-700 text-white focus:border-emerald-500'
                    : 'bg-white border-gray-300 text-gray-900 focus:border-emerald-500'
                } focus:outline-none`}
                placeholder="contact@mosque.com (optional until handover)"
              />
              <p className={`mt-1.5 text-xs ${
                isDarkMode ? 'text-gray-500' : 'text-gray-500'
              }`}>
                Optional - Required when transferring to mosque admin
              </p>
            </div>

            <div>
              <label className={`block text-sm font-medium mb-2 ${
                isDarkMode ? 'text-gray-300' : 'text-gray-700'
              }`}>
                Phone Number
              </label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                className={`w-full px-4 py-2.5 rounded-xl border transition-colors ${
                  isDarkMode
                    ? 'bg-[#0A0A0A] border-gray-700 text-white focus:border-emerald-500'
                    : 'bg-white border-gray-300 text-gray-900 focus:border-emerald-500'
                } focus:outline-none`}
              />
            </div>

            <div>
              <label className={`block text-sm font-medium mb-2 ${
                isDarkMode ? 'text-gray-300' : 'text-gray-700'
              }`}>
                Website
              </label>
              <input
                type="url"
                name="website"
                value={formData.website}
                onChange={handleInputChange}
                className={`w-full px-4 py-2.5 rounded-xl border transition-colors ${
                  isDarkMode
                    ? 'bg-[#0A0A0A] border-gray-700 text-white focus:border-emerald-500'
                    : 'bg-white border-gray-300 text-gray-900 focus:border-emerald-500'
                } focus:outline-none`}
              />
            </div>

            <div>
              <label className={`block text-sm font-medium mb-2 ${
                isDarkMode ? 'text-gray-300' : 'text-gray-700'
              }`}>
                Photo URL
              </label>
              <input
                type="url"
                name="photo"
                value={formData.photo}
                onChange={handleInputChange}
                className={`w-full px-4 py-2.5 rounded-xl border transition-colors ${
                  isDarkMode
                    ? 'bg-[#0A0A0A] border-gray-700 text-white focus:border-emerald-500'
                    : 'bg-white border-gray-300 text-gray-900 focus:border-emerald-500'
                } focus:outline-none`}
              />
            </div>
          </div>
        </div>

        {/* Submit Buttons */}
        <div className="flex gap-4">
          <button
            type="button"
            onClick={() => navigate('/admin/mosques')}
            className={`flex-1 px-6 py-3 rounded-xl transition-colors ${
              isDarkMode
                ? 'bg-gray-800 text-white hover:bg-gray-700'
                : 'bg-gray-200 text-gray-900 hover:bg-gray-300'
            }`}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className={`flex-1 px-6 py-3 rounded-xl transition-colors flex items-center justify-center gap-2 ${
              isDarkMode
                ? 'bg-emerald-500 text-black hover:bg-emerald-400 disabled:bg-emerald-500/50'
                : 'bg-gray-900 text-white hover:bg-gray-800 disabled:bg-gray-400'
            }`}
          >
            {saving ? (
              <>
                <Loader className="w-5 h-5 animate-spin" />
                Saving...
              </>
            ) : (
              'Save Changes'
            )}
          </button>
        </div>
      </form>

      {/* Transfer Modal */}
      {showTransferModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className={`max-w-md w-full rounded-[24px] p-6 ${
            isDarkMode ? 'bg-[#111111]' : 'bg-white'
          }`}>
            <h3 className={`text-xl font-bold mb-4 ${
              isDarkMode ? 'text-white' : 'text-gray-900'
            }`}>
              Transfer to Mosque Admin
            </h3>

            <p className={`mb-4 text-sm ${
              isDarkMode ? 'text-gray-400' : 'text-gray-600'
            }`}>
              Select an approved mosque administrator to transfer ownership
            </p>

            <select
              value={selectedAdminId}
              onChange={(e) => setSelectedAdminId(e.target.value)}
              className={`w-full px-4 py-2.5 rounded-xl border mb-4 ${
                isDarkMode
                  ? 'bg-[#0A0A0A] border-gray-700 text-white'
                  : 'bg-white border-gray-300 text-gray-900'
              }`}
            >
              <option value="">Select admin...</option>
              {mosqueAdmins.map(admin => (
                <option key={admin.id} value={admin.id}>
                  {admin.displayName} - {admin.email}
                </option>
              ))}
            </select>

            <div className="flex gap-3">
              <button
                onClick={() => setShowTransferModal(false)}
                className={`flex-1 px-4 py-2 rounded-xl ${
                  isDarkMode
                    ? 'bg-gray-800 text-white hover:bg-gray-700'
                    : 'bg-gray-200 text-gray-900 hover:bg-gray-300'
                }`}
              >
                Cancel
              </button>
              <button
                onClick={handleTransfer}
                disabled={!selectedAdminId || saving}
                className={`flex-1 px-4 py-2 rounded-xl ${
                  isDarkMode
                    ? 'bg-emerald-500 text-black hover:bg-emerald-400 disabled:bg-emerald-500/50'
                    : 'bg-gray-900 text-white hover:bg-gray-800 disabled:bg-gray-400'
                }`}
              >
                {saving ? 'Transferring...' : 'Transfer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EditMosque;
